/**
 * SplitScroll Module — Sticky Media + Scrolling Text Cards
 *
 * Classic scrollytelling: video/image stays fixed ("sticky") on one side,
 * text cards scroll past on the other side with fade-in animation.
 *
 * Panel HTML (Ghost Editor):
 *   <section class="story-panel" data-story-type="splitscroll"
 *            data-media-side="right" data-step-height="100">
 *     <video data-story-autoplay playsinline muted loop preload="none">
 *       <source src="https://ghost.../video.mp4" type="video/mp4">
 *     </video>
 *     <div class="splitscroll__step"><h3>Scene 1</h3><p>Text...</p></div>
 *     <div class="splitscroll__step"><p>Scene 2...</p></div>
 *   </section>
 *
 * Features:
 *   - CSS Grid with sticky media column
 *   - Configurable media side (left/right via data-media-side)
 *   - Step fade-in via IntersectionObserver (one-shot)
 *   - Scroll speed via data-step-height (vh units)
 *   - Works in both scroll and slide (snap) templates
 *   - Video handled by existing VideoAutoplay module
 *   - Reduced motion: no transitions
 *
 * Events: Listens to story:panel:activated / deactivated
 * Registration: core.registerModule('splitscroll', this)
 *
 * File: assets/js/story/story-module-splitscroll.js
 */

(function(window) {
  'use strict';

  var DEBUG = false;
  function log() {
    if (DEBUG && window.console) {
      var args = Array.prototype.slice.call(arguments);
      args.unshift('[SplitScroll]');
      console.log.apply(console, args);
    }
  }


  /* ========================================
     Constructor
     ======================================== */

  function StorySplitScroll(core) {
    this.core = core;
    this.instances = [];
    this._observers = [];

    // MediaManager contract properties
    this.name = 'splitscroll';
    this.type = 'visual';
    this.isPlaying = false;
    this.panel = null;
    this.element = null;

    this._init();
    core.registerModule('splitscroll', this);
    log('Module registered');
  }


  /* ========================================
     MediaManager contract stubs
     ======================================== */

  StorySplitScroll.prototype.init = function() {};
  StorySplitScroll.prototype.start = function() {};
  StorySplitScroll.prototype.pause = function() {};
  StorySplitScroll.prototype.stop = function() {};

  StorySplitScroll.prototype.destroy = function() {
    // Disconnect all step IntersectionObservers
    for (var i = 0; i < this._observers.length; i++) {
      this._observers[i].disconnect();
    }
    this._observers = [];
    this.instances = [];
    log('Destroyed');
  };


  /* ========================================
     Init — find and setup splitscroll panels
     ======================================== */

  StorySplitScroll.prototype._init = function() {
    var panels = document.querySelectorAll('.story-panel[data-story-type="splitscroll"]');

    for (var i = 0; i < panels.length; i++) {
      this._setupSplitScroll(panels[i]);
    }

    log('Found', this.instances.length, 'splitscroll panels');
  };


  /* ========================================
     DOM Restructuring
     Flat user HTML → Grid layout
     ======================================== */

  StorySplitScroll.prototype._setupSplitScroll = function(panel) {
    // Find media element: first <video> or <img> NOT inside a step
    var media = null;
    var children = panel.children;
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      if (child.classList && child.classList.contains('splitscroll__step')) continue;
      if (child.tagName === 'VIDEO' || child.tagName === 'IMG') {
        media = child;
        break;
      }
      // Also check for picture element
      if (child.tagName === 'PICTURE') {
        media = child;
        break;
      }
    }

    if (!media) {
      log('No media found in panel, skipping');
      return;
    }

    // Collect all steps
    var steps = panel.querySelectorAll('.splitscroll__step');
    if (steps.length === 0) {
      log('No steps found in panel, skipping');
      return;
    }

    // Preserve hub button if present
    var hubBtn = panel.querySelector('[data-goto-hub]');

    // Preserve audio-bubbles + caption containers — im Original-HTML sitzen
    // sie auf dem Medium; sie sollen in die Media-Spalte mitwandern, statt
    // vom DOM-Wipe weiter unten zerstört zu werden.
    var bubbles = Array.prototype.slice.call(panel.querySelectorAll('.audio-bubble'));
    var captions = Array.prototype.slice.call(panel.querySelectorAll('.audio-bubble-caption'));

    // Read configuration
    var mediaSide = panel.getAttribute('data-media-side') || 'right';
    var stepHeightVh = parseFloat(panel.getAttribute('data-step-height')) || 100;

    // C01 opt-out: mark media images
    if (media.tagName === 'IMG') {
      media.setAttribute('data-no-dynamic', '');
    }
    if (media.tagName === 'PICTURE') {
      var imgs = media.querySelectorAll('img');
      for (var j = 0; j < imgs.length; j++) {
        imgs[j].setAttribute('data-no-dynamic', '');
      }
    }

    // Build grid structure
    var grid = document.createElement('div');
    grid.className = 'splitscroll__grid splitscroll__grid--media-' + mediaSide;

    // Propagate data-media-overlay from panel to grid (fullscreen video/image on all devices)
    if (panel.hasAttribute('data-media-overlay')) {
      grid.setAttribute('data-media-overlay', '');
    }

    // Media column
    var mediaCol = document.createElement('div');
    mediaCol.className = 'splitscroll__media';
    mediaCol.appendChild(media);

    // Audio-Bubbles + Caption-Container über dem Sticky-Medium platzieren.
    // .splitscroll__media ist position:relative → die Bubbles ankern hier.
    for (var b = 0; b < bubbles.length; b++) {
      mediaCol.appendChild(bubbles[b]);
    }
    for (var c = 0; c < captions.length; c++) {
      mediaCol.appendChild(captions[c]);
    }

    // Steps column
    var stepsCol = document.createElement('div');
    stepsCol.className = 'splitscroll__steps';

    for (var k = 0; k < steps.length; k++) {
      var step = steps[k];

      // Set per-step height from data attribute
      step.style.setProperty('--splitscroll-step-height', stepHeightVh + 'vh');

      // Wrap step content for fade-in
      var content = document.createElement('div');
      content.className = 'splitscroll__step-content';

      // Move all children into content wrapper
      while (step.firstChild) {
        content.appendChild(step.firstChild);
      }
      step.appendChild(content);

      stepsCol.appendChild(step);
    }

    // Assemble grid
    grid.appendChild(mediaCol);
    grid.appendChild(stepsCol);

    // Clear panel and insert grid
    // Remove everything except the grid we're about to add
    while (panel.firstChild) {
      panel.removeChild(panel.firstChild);
    }
    panel.appendChild(grid);

    // Re-append hub button if it was present
    if (hubBtn) {
      panel.appendChild(hubBtn);
    }

    // Snap-mode: disable snap on the panel itself, enable on steps
    var container = panel.closest('.story-container--snap');
    if (container) {
      panel.style.scrollSnapAlign = 'none';
      log('Snap mode detected, delegating snap to steps');
    }

    // Re-register moved video with core observer.
    // VideoAutoplay sets up IntersectionObservers BEFORE SplitScroll runs.
    // Moving the video in the DOM invalidates the observer — re-observe to fix.
    if (media.tagName === 'VIDEO' && media.hasAttribute('data-story-autoplay')) {
      try {
        this.core.unobserve([media], 'media');
        this.core.observe([media], 'media');
      } catch (e) {
        log('Could not re-observe video:', e);
      }
    }

    // Setup step fade-in observer
    this._setupStepObserver(steps);

    // Store instance
    this.instances.push({
      panel: panel,
      grid: grid,
      media: media,
      mediaCol: mediaCol,
      stepsCol: stepsCol,
      steps: steps,
      mediaSide: mediaSide
    });

    log('Setup complete:', mediaSide, 'media,', steps.length, 'steps');
  };


  /* ========================================
     Step IntersectionObserver — Fade-In
     ======================================== */

  StorySplitScroll.prototype._setupStepObserver = function(steps) {
    var prefersReducedMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // If reduced motion, make all steps visible immediately
    if (prefersReducedMotion) {
      for (var i = 0; i < steps.length; i++) {
        steps[i].classList.add('is-visible');
      }
      return;
    }

    var observer = new IntersectionObserver(function(entries) {
      for (var j = 0; j < entries.length; j++) {
        var entry = entries[j];
        if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);  // One-shot
        }
      }
    }, {
      threshold: [0, 0.4],
      rootMargin: '0px'
    });

    for (var k = 0; k < steps.length; k++) {
      observer.observe(steps[k]);
    }

    this._observers.push(observer);
  };


  /* ========================================
     Expose
     ======================================== */

  window.StoryModuleSplitScroll = StorySplitScroll;

})(window);
